import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import MediaAPI from "../../../api/MediaAPI";
import { DRAG_FORMAT_UNKNOWN, DRAG_TYPE } from "../../../constants/dragTypes";
import { AD_BREAK, MEDIA_FORMATS } from "../../../constants/media";
import DropzoneListContainer from "../../dropzoneList/DropzoneListContainer";
import DropzoneMediaItem from "../dropzoneList/DropzoneMediaItem";

import type { JSX } from "react";

import type { PlaylistDropzoneMedia } from "../../../types/fast";

interface FASTPlaylistDropZoneProps {
  initialMediaList?: PlaylistDropzoneMedia[];
  onPlaylistChange: (playlistMediaList: PlaylistDropzoneMedia[]) => void;
  isLoading: boolean;
}

export default function FASTPlaylistDropZone({
  initialMediaList,
  onPlaylistChange,
  isLoading,
}: FASTPlaylistDropZoneProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: metadataData } = useQuery(getMediasMetadataListQuery);

  const acceptedMediaFormatList = useMemo(
    () =>
      metadataData?.formats.filter(
        (format) => ![MEDIA_FORMATS.ADPOD, AD_BREAK].includes(format.key),
      ) ?? [],
    [metadataData],
  );

  const acceptList = useMemo(
    () =>
      acceptedMediaFormatList
        .map((format) => `${DRAG_TYPE.MEDIA_LIBRARY_ITEM}-${format.key}`)
        .concat([`${DRAG_TYPE.MEDIA_LIBRARY_ITEM}-${DRAG_FORMAT_UNKNOWN}`]),
    [acceptedMediaFormatList],
  );

  return (
    <DropzoneListContainer
      acceptList={acceptList}
      classNamesContainer="p-3 overflow-auto grow"
      initialItemList={initialMediaList}
      isLoading={isLoading}
      onChange={onPlaylistChange}
      renderItem={DropzoneMediaItem}
      placeholder={{
        text: t("playlistDropzonePlaceholderText"),
        withIcon: true,
      }}
    />
  );
}
